import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, Eye, EyeOff, Lock, RefreshCw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { BACKEND_URL } from '../../config';

const SecretsSection = () => {
  const [secretsList, setSecretsList] = useState([]);
  const [secretsLoading, setSecretsLoading] = useState(false);
  const [secretsSaving, setSecretsSaving] = useState(false);
  const [secretsRevealed, setSecretsRevealed] = useState(false);
  const [secretDrafts, setSecretDrafts] = useState({});
  const [showSecret, setShowSecret] = useState({});

  const fetchSecrets = async (reveal = false) => {
    try {
      setSecretsLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/secrets?reveal=${reveal ? 'true' : 'false'}`);
      const data = await res.json();
      if (data.status === 'success') {
        setSecretsList(data.secrets || []);
        setSecretsRevealed(!!data.revealed);
        const drafts = {};
        (data.secrets || []).forEach((s) => { drafts[s.key] = s.value || ''; });
        setSecretDrafts(drafts);
      } else {
        toast.error('Failed to load secrets');
      }
    } catch (e) {
      toast.error('Error loading secrets: ' + e.message);
    } finally {
      setSecretsLoading(false);
    }
  };

  const handleSaveSecret = async (key) => {
    const value = (secretDrafts[key] || '').trim();
    if (!value) {
      toast.error(`${key} cannot be empty`);
      return;
    }
    if (value.includes('*') && !secretsRevealed) {
      toast.error("Please reveal the secret before editing so you don't save the masked value");
      return;
    }
    try {
      setSecretsSaving(true);
      const res = await fetch(`${BACKEND_URL}/api/secrets`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [key]: value } }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        toast.success(`${key} updated successfully`);
        fetchSecrets(secretsRevealed);
      } else {
        toast.error(data.detail || 'Failed to update secret');
      }
    } catch (e) {
      toast.error('Error saving secret: ' + e.message);
    } finally {
      setSecretsSaving(false);
    }
  };

  useEffect(() => {
    fetchSecrets(false);
  }, []);

  return (
    <div className="space-y-6" data-testid="secrets-section">
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Deployment Secrets
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSecrets(!secretsRevealed)}
                disabled={secretsLoading}
                data-testid="secrets-toggle-reveal-btn"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                {secretsRevealed ? (
                  <><EyeOff className="w-4 h-4 mr-1" /> Hide All</>
                ) : (
                  <><Eye className="w-4 h-4 mr-1" /> Reveal All</>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchSecrets(secretsRevealed)}
                disabled={secretsLoading}
                data-testid="secrets-refresh-btn"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <RefreshCw className={`w-4 h-4 ${secretsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <Alert className="mb-6 border-amber-300 bg-amber-50">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <AlertTitle className="text-amber-900">Sensitive Data</AlertTitle>
            <AlertDescription className="text-amber-800">
              Changes are written to <code className="bg-amber-100 px-1 rounded">backend/.env</code> and applied to the running process immediately. Rotate keys carefully.
            </AlertDescription>
          </Alert>

          {secretsLoading && secretsList.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-600" />
            </div>
          ) : (
            <div className="space-y-5">
              {secretsList.map((secret) => {
                const isMasked = !secretsRevealed;
                const inputVisible = showSecret[secret.key] || secretsRevealed;
                return (
                  <div
                    key={secret.key}
                    className="border border-slate-200 rounded-lg p-4 bg-slate-50"
                    data-testid={`secret-row-${secret.key}`}
                  >
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div>
                        <label className="text-sm font-bold text-slate-800 font-mono">
                          {secret.key}
                        </label>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {secret.is_set
                            ? `Currently set • length ${secret.length}`
                            : 'Not set'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {!secretsRevealed && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setShowSecret((prev) => ({ ...prev, [secret.key]: !prev[secret.key] }))
                            }
                            data-testid={`secret-show-toggle-${secret.key}`}
                          >
                            {inputVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-stretch gap-2">
                      <input
                        type={inputVisible ? 'text' : 'password'}
                        value={secretDrafts[secret.key] ?? ''}
                        onChange={(e) =>
                          setSecretDrafts((prev) => ({ ...prev, [secret.key]: e.target.value }))
                        }
                        placeholder={isMasked ? 'Reveal All to view current value, or enter a new one' : `Enter value for ${secret.key}`}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid={`secret-input-${secret.key}`}
                      />
                      <Button
                        onClick={() => handleSaveSecret(secret.key)}
                        disabled={secretsSaving}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid={`secret-save-btn-${secret.key}`}
                      >
                        {secretsSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <><Save className="w-4 h-4 mr-1" /> Save</>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecretsSection;
